import { useEffect, useRef, useState } from 'react'

export default function ScanCapture({
  onCapture,
  onCancel,
  onManual,
  busy = false,
  captureKind = 'product',
}) {
  const videoRef = useRef(null)
  const fileRef = useRef(null)
  const streamRef = useRef(null)
  const [phase, setPhase] = useState('prompt')
  const [message, setMessage] = useState('La caméra sert uniquement à photographier le produit.')
  const [preview, setPreview] = useState({ blob: null, url: '' })

  const guides = {
    product: {
      title: 'Placez le produit ou son code-barres dans le cadre.',
      detail: 'Gardez la marque, la référence et les informations utiles bien nettes.',
    },
    barcode: {
      title: 'Placez le code-barres dans le cadre.',
      detail: 'Gardez les chiffres et toutes les barres visibles, sans reflet.',
    },
    label: {
      title: 'Cadrez le modèle et la variante',
      detail: 'Incluez la référence, la taille, la couleur et les mentions utiles.',
    },
    price: {
      title: 'Cadrez le prix et les mentions associées',
      detail: 'Incluez la devise et l’étiquette complète.',
    },
  }
  const guide = guides[captureKind] || guides.product

  const stopStream = () => {
    streamRef.current?.getTracks?.().forEach((track) => track.stop())
    streamRef.current = null
  }

  const clearPreview = () => {
    if (preview.url) URL.revokeObjectURL(preview.url)
    setPreview({ blob: null, url: '' })
  }

  const startCamera = async () => {
    clearPreview()
    if (!navigator.mediaDevices?.getUserMedia) {
      setPhase('unavailable')
      setMessage('Caméra indisponible. Vous pouvez choisir une photo ou ajouter le produit à la main.')
      return
    }
    setPhase('loading')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) videoRef.current.srcObject = stream
      await videoRef.current?.play?.()
      setPhase('ready')
    } catch (error) {
      const denied = error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError'
      setPhase(denied ? 'denied' : 'unavailable')
      setMessage(
        denied
          ? 'Autorisation refusée. Vous pouvez choisir une image dans la photothèque ou saisir le produit manuellement.'
          : 'Caméra indisponible. Choisissez une photo ou ajoutez le produit à la main.',
      )
    }
  }

  useEffect(() => () => {
    stopStream()
    if (preview.url) URL.revokeObjectURL(preview.url)
  }, [preview.url])

  const takePhoto = async () => {
    const video = videoRef.current
    if (!video || video.readyState < 2) return
    setPhase('capturing')
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth || 1280
    canvas.height = video.videoHeight || 720
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92))
    if (blob) {
      stopStream()
      setPreview({ blob, url: URL.createObjectURL(blob) })
      setPhase('preview')
    } else {
      setPhase('ready')
    }
  }

  const onFile = (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (file) {
      stopStream()
      clearPreview()
      setPreview({ blob: file, url: URL.createObjectURL(file) })
      setPhase('preview')
    }
  }

  const retake = async () => {
    clearPreview()
    await startCamera()
  }

  const usePhoto = () => {
    if (!preview.blob) return
    setPhase('analyzing')
    onCapture(preview.blob)
  }

  const cameraIcon = (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8.5 6.5 10 4.8h4l1.5 1.7H19a2 2 0 0 1 2 2v8.5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8.5a2 2 0 0 1 2-2h3.5Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="12.5" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  )

  return (
    <>
      <div className={`scan-capture-stage is-${phase}`}>
        {preview.url ? (
          <img className="scan-capture-preview" src={preview.url} alt="Aperçu de la photo capturée" />
        ) : (
          <video
            ref={videoRef}
            className={`scan-capture-video${phase === 'ready' || phase === 'capturing' ? ' is-live' : ''}`}
            playsInline
            muted
            autoPlay
          />
        )}
        {phase === 'ready' || phase === 'capturing' ? (
          <>
            <div className="scan-capture-frame" aria-hidden="true">
              <i /><i /><i /><i />
            </div>
            <p className="scan-capture-live-hint">{guide.title}</p>
          </>
        ) : null}
        {phase === 'preview' ? (
          <div className="scan-preview-badge">Aperçu</div>
        ) : null}
        {phase === 'loading' || phase === 'analyzing' ? (
          <div className="scan-capture-status is-busy" role="status" aria-live="polite">
            <span className="scan-camera-spinner" aria-hidden="true" />
            <strong>{phase === 'analyzing' ? 'Préparation de l’analyse…' : 'Activation de la caméra…'}</strong>
          </div>
        ) : null}
        {phase === 'prompt' || phase === 'denied' || phase === 'unavailable' ? (
          <div className="scan-capture-idle">
            <button type="button" className="scan-camera-enable" onClick={startCamera}>
              {cameraIcon}
              <span>{phase === 'prompt' ? 'Activer la caméra' : 'Réessayer'}</span>
            </button>
            {phase === 'prompt' ? null : <p>{message}</p>}
          </div>
        ) : null}
      </div>
      {phase === 'preview' ? (
        <div className="scan-preview-actions">
          <button type="button" className="scan-control-button" onClick={retake}>
            Reprendre
          </button>
          <button type="button" className="scan-control-button is-primary" onClick={usePhoto} disabled={busy}>
            Utiliser cette photo
          </button>
        </div>
      ) : (
        <div className="scan-capture-actions">
          <button type="button" className="scan-capture-side-action" onClick={() => fileRef.current?.click()} disabled={busy}>
            <img src="/Icon photo size select actual.png" alt="" />
            <span>Photothèque</span>
          </button>
          <button type="button" className="scan-shutter" onClick={takePhoto} disabled={phase !== 'ready' || busy} aria-label="Prendre la photo">
            <span aria-hidden="true" />
          </button>
          <button type="button" className="scan-capture-side-action" onClick={onManual} disabled={busy}>
            <span className="scan-capture-action-glyph" aria-hidden="true">✎</span>
            <span>Saisie manuelle</span>
          </button>
        </div>
      )}
      {captureKind === 'product' ? null : (
        <button type="button" className="scan-capture-cancel" onClick={onCancel}>Retour</button>
      )}
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
    </>
  )
}
